/**
 * CameraClusterMarker
 *
 * Map marker component that renders a cluster badge for grouped cameras.
 * Shows the count of cameras and has alert styling when cluster contains cameras with alerts.
 */

import React, { memo } from 'react';
import { Typography } from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import classNames from 'classnames';
import styles from './CameraClusterMarker.module.css';

interface CameraClusterMarkerProps {
  cluster: {
    id: number;
    geometry: {
      coordinates: [number, number]; // [lng, lat]
    };
    properties: {
      cluster: true;
      cluster_id: number;
      point_count: number;
      hasAlerts: number; // Count of cameras with alerts in cluster
    };
  };
  /** Latitude for google-map-react positioning */
  lat: number;
  /** Longitude for google-map-react positioning */
  lng: number;
  onClick?: (clusterId: number, coordinates: [number, number]) => void;
}

/**
 * Renders a cluster badge for grouped cameras on the map.
 */
const CameraClusterMarker: React.FC<CameraClusterMarkerProps> = ({
  cluster,
  onClick,
}) => {
  const count = cluster.properties.point_count;
  const alertCount = cluster.properties.hasAlerts ?? 0;
  const hasAlerts = alertCount > 0;

  // Determine size class based on camera count
  let sizeClass = styles.small;
  if (count > 15) {
    sizeClass = styles.large;
  } else if (count > 5) {
    sizeClass = styles.medium;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(cluster.properties.cluster_id, cluster.geometry.coordinates);
  };

  const title = `${count} camera${count !== 1 ? 's' : ''}${hasAlerts ? ` (${alertCount} with alert${alertCount !== 1 ? 's' : ''})` : ''}`;

  return (
    <div
      className={classNames(styles.cluster, sizeClass, {
        [styles.hasAlerts]: hasAlerts,
      })}
      onClick={handleClick}
      title={title}
    >
      <VideocamIcon className={styles.icon} />
      <Typography className={styles.count}>{count}</Typography>
    </div>
  );
};

/**
 * Custom comparison function for React.memo
 * Only re-render when values that actually affect the marker change
 */
function arePropsEqual(
  prevProps: CameraClusterMarkerProps,
  nextProps: CameraClusterMarkerProps
): boolean {
  const prevCluster = prevProps.cluster;
  const nextCluster = nextProps.cluster;

  // Compare cluster ID and count
  if (
    prevCluster.properties.cluster_id !== nextCluster.properties.cluster_id ||
    prevCluster.properties.point_count !== nextCluster.properties.point_count ||
    prevCluster.properties.hasAlerts !== nextCluster.properties.hasAlerts
  ) {
    return false;
  }

  // Compare position
  if (
    prevCluster.geometry.coordinates[0] !== nextCluster.geometry.coordinates[0] ||
    prevCluster.geometry.coordinates[1] !== nextCluster.geometry.coordinates[1]
  ) {
    return false;
  }

  return true;
}

export default memo(CameraClusterMarker, arePropsEqual);
